import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import styled from '@mui/material/styles/styled';

import { debounce } from '@mui/material/utils';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpItem } from '../model/HelpItem';
import { HelpItemManager } from '../model/HlelpItemManager';
import { buildUrl } from './UrlBuilder';

// 惨痛的教训：不要在组件内部创建实例
// AI 生成代码时将StyledAutocomplete组件的代码放置到HelpSearch组件内部,导致HelpSearch重复渲染
// 每输入一个字符，页面都会刷新，导致无法输入，花了4小时左右才找到根源
const StyledAutocomplete = styled(Autocomplete<HelpItem>)(({ theme }) => ({
    '& .MuiAutocomplete-inputRoot': {
      backgroundColor: theme.palette.background.default, // 设置背景颜色
      borderRadius: theme.shape.borderRadius, // 设置圆角
      padding: 0, // 移除多余的内边距
      height: theme.spacing(5), // 设置输入框高度
      border: `1px solid ${theme.palette.divider}`, // 添加边框颜色
      transition: 'border-color 0.3s', // 添加边框颜色变化的过渡效果
      '&:hover': {
        borderColor: theme.palette.primary.dark, // 悬停时的边框颜色
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none', // 隐藏默认的边框
      },
    },
    '& .MuiAutocomplete-input': {
      padding: '8px', // 调整输入框的内边距
    },
    '& .MuiAutocomplete-popupIndicator': {
      color: theme.palette.text.secondary, // 设置下拉箭头颜色
      '&:hover': {
        color: theme.palette.primary.main, // 悬停时箭头颜色
      },
    },
    '& .MuiAutocomplete-clearIndicator': {
      color: theme.palette.text.secondary, // 设置清除按钮颜色
      '&:hover': {
        color: theme.palette.error.main, // 悬停时清除按钮颜色
      },
    },
    '& .MuiAutocomplete-option': {
      backgroundColor: theme.palette.background.paper, // 设置选项背景色
      '&[aria-selected="true"]': {
        backgroundColor: theme.palette.action.selected, // 选中时的背景色
      },
      '&:hover': {
        backgroundColor: theme.palette.action.hover, // 悬停时的背景色
      },
    },
    '& .MuiAutocomplete-listbox': {
      padding: 0, // 移除下拉菜单的内边距
      borderRadius: theme.shape.borderRadius, // 下拉菜单的圆角
    },
}));
interface SearchProps {
    apiVersion: string;
    maxResults?: number;
  }
export const HelpSearch : React.FC<SearchProps> = ({apiVersion, maxResults}) =>  {
    const [, setInputValue] = React.useState('');
    const [options, setOptions] = useState<HelpItem[]>([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // 创建防抖搜索函数
    const handleSearch = debounce(async(term: string) => {
            if (term.length < 2) {
                setOptions([]);
                return;
            }

            setLoading(true);
            const manager = HelpItemManager.getInstance();
            const results = await manager.searchHelpItems(term, maxResults || 10);
            setOptions(results);
            setLoading(false);
        }, 500);

    // 处理搜索输入
    const onInputChange = (_event: React.SyntheticEvent, value: string) => {
        setInputValue(value);
        handleSearch(value);
    };

    // 处理选择
    const onChange = (_event: React.SyntheticEvent, value: HelpItem | null) => {
        if (value) {
            navigate(buildUrl(apiVersion, value.url));
        }
    };


    return (
        <StyledAutocomplete
            sx={{ width: '100%' }}
            getOptionLabel={(option) => option.title}
            filterOptions={(x) => x}
            options={options}
            autoComplete
            includeInputInList
            filterSelectedOptions
            onInputChange={onInputChange}
            onChange={onChange}
            noOptionsText="输入关键字或者正则表达式"
            loading={loading}
            renderOption={(props, option) => (
                <li {...props} key={`${option.title}}`}>
                  {option.title}
                </li>
              )}
            renderInput={(params) => (
                <TextField
                  {...params}
                  label="搜索"
                  fullWidth
                  slotProps={{
                    input: {
                    ...params.InputProps,
                    endAdornment: (
                        <React.Fragment>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                        </React.Fragment>
                    )},
                }}
                />            
            )}



        />
    );
};

